<template>
  <DefaultPageContainer
    :cnc-ads-page="cncAdsPage"
    :breadcrumb-text="authorStore.author?.fullname as string"
  >
    <section class="section">
      <DefaultTwoColumnsContent
        right-container-class="section__right-col--mobile-hide"
      >
        <template #left>
          <!-- Author info -->
          <div v-if="authorStore.author && !authorStore.isLoading" class="author-id">
            <div class="flex justify-between">
              <div class="badge badge--author-id">
                <div class="badge__aside">
                  <img
                    loading="lazy"
                    v-if="authorStore.author?.imageAsset?.urlWithProtocol && !imageError"
                    class="object-cover h-full w-full"
                    :src="authorStore.author?.imageAsset?.urlWithProtocol"
                    :alt="authorStore.author?.fullname || ''"
                    width="60"
                    height="60"
                    @error="replaceByDefault"
                  >
                  <!--no profile-pic -->
                  <DefaultIconsAccount v-if="!authorStore.author?.imageAsset?.urlWithProtocol || imageError" />
                  <!--no profile-pic -->
                </div>
                <div class="badge__body">
                  <div class="text-2xl font-bold leading-6">{{authorStore.author?.fullname}}</div>
                  <div
                    v-if="authorStore.author?.profession"
                    class="text-sm"
                  >
                    {{authorStore.author?.profession}}
                  </div>
                </div>
              </div>
              <div class="hidden sm:flex flex-col gap-2 items-end">
                <DefaultSocialList
                  :items="socialList()"
                />
                <div v-if="authorStore.author?.email">
                  <NuxtLink :to="`mailto:${authorStore.author?.email}`" class="link link--author-mail">{{authorStore.author?.email}}</NuxtLink>
                </div>
              </div>
            </div>

            <div v-if="authorStore.author?.biography" v-html="authorStore.author?.biography" />

            <div class="sm:hidden flex flex-col gap-2 items-start">
              <DefaultSocialList
                :items="socialList()"
              />
              <div v-if="authorStore.author?.email">
                <NuxtLink :to="`mailto:${authorStore.author?.email}`" class="link link--author-mail">{{authorStore.author?.email}}</NuxtLink>
              </div>
            </div>
          </div>
          <!-- Skeleton - loading -->
          <div v-else-if="authorStore.isLoading">
            <USkeleton class="w-full h-40 rounded-2xl" />
          </div>

          <h1
            v-if="articleStore.getFilteredArticlesCount() || articleStore.isLoading"
            class="h4"
          >
            {{ $t('author-page.author_articles') }}
          </h1>

<!--          <div v-if="!articleStore.getFilteredArticlesCount() && !articleStore.isLoading">-->
<!--            <DefaultErrorsNoResultsFound />-->
<!--          </div>-->
          <div v-if="articleStore.getFilteredArticlesCount() > 0 || !articleStore.isLoading" class="section__articles-container">

            <!-- article list -->
            <DefaultArticlesListing
              :articles="articleStore.getOnlyFilteredArticles(0, (page * pageArticlesCount))"
              :ad-page="cncAdsPage"
              :articles-type="ArticleComponentType.SEARCH"
            />

            <!-- Loading NEXT page -->
            <div
              v-if="articleStore.isLoading"
              v-for="item in 10"
              :key="`skeleton_${item}`"
              class="article-wrapper"
            >
              <DefaultSkeletonsArticleCard
                :type="ArticleComponentType.SEARCH"
              />
            </div>

            <!-- Next page -->
            <div
              v-if="articleStore.getArticlesCount() >= articleStore.limit"
              class="button-more-container"
            >
              <DefaultArticlesButtonMore
                :label="$t('articles.button_more')"
                :disabled="articleStore.isLoading"
                @click="nextPage"
              />
            </div>
          </div>
          <!-- Loading page content -->
          <div v-else-if="articleStore.isLoading" class="section__articles-container">
            <!-- Skeletons -->
            <div
              v-if="articleStore.isLoading"
              v-for="item in 10"
              :key="`skeleton_${item}`"
              class="article-container"
            >
              <DefaultSkeletonsArticleCard
                :type="ArticleComponentType.SEARCH"
              />
            </div>
          </div>
        </template>
        <template #right>
          <!-- CNC Ads -->
          <div class="advertisement-right-col h-full">
            <div class="sticky-sidebar-ad-wrapper">
              <DefaultAdsCncWrapper
                :position="UniFeAdsPosition.COLUMN_STICKY"
                :page="cncAdsPage"
              />
            </div>
          </div>
        </template>
      </DefaultTwoColumnsContent>
    </section>
  </DefaultPageContainer>
</template>

<script setup lang="ts">
import {ARTICLE_QUERY_TYPE, useArticlesStore} from "~/stores/useArticlesStore"
import {ArticleStoreType} from "~/libs/constants/ArticleStoreType"
import ArticleComponentType from "~/libs/constants/ArticleComponentType"
import type ArticleFilters from "~/libs/interfaces/ArticleFilters"
import {AUTHOR_ID_PREFIX} from "~/libs/constants/constants"
import {useShortItemId} from "~/composables/useShortItemId"
import {AUTHOR_QUERY_TYPE, useAuthorsStore} from "~/stores/useAuthorsStore"
import {AuthorStoreType} from "~/libs/constants/AuthorStoreType"
import {useApiIdConverter} from "~/composables/useApiIdConverter"
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"
import type SocialItem from "~/libs/interfaces/SocialItem"
import {SocialList} from "~/libs/constants/SocialList"
import type AuthorFilters from "~/libs/interfaces/AuthorFilters"

// i18n routing
definePageMeta({
  name: 'authors-authorUrl',
})
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    cs: '/autor/[authorUrl]',
    en: '/author/[authorUrl]'
  }
})

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const cncAdsPage = CncAdsPage.AUTHOR

const authorId = useShortItemId(route.params.authorUrl as string, '-')
const fullAuthorId = `${AUTHOR_ID_PREFIX}${authorId}`

const articleStore = useArticlesStore(ArticleStoreType.AUTHOR, authorId)
const authorStore = useAuthorsStore(AuthorStoreType.AUTHOR)

// Set SEO meta
useSeoWrapper().setSeoMeta({
  title: authorStore.author?.fullname,
})

/**
 * Author info
 */

const imageError = ref(false)

const replaceByDefault = () => {
  imageError.value = true
}

const id = useApiIdConverter().apiIdToUuid(fullAuthorId) // WTF - why is here different ID than API returns?? fullAuthorId VS id
if (!id) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}
const authorFilters = {
  ids: [id]
}

const socialList = ():SocialItem[] => {
  const list: SocialItem[] = []
  // Facebook
  if (authorStore.author?.facebook) {
    list.push({
      name: SocialList.FACEBOOK,
      id: SocialList.FACEBOOK,
      url: authorStore.author?.facebook
    })
  }
  // Instagram
  if (authorStore.author?.instagram) {
    list.push({
      name: SocialList.INSTAGRAM,
      id: SocialList.INSTAGRAM,
      url: authorStore.author?.instagram
    })
  }
  // Twitter
  if (authorStore.author?.twitter) {
    list.push({
      name: SocialList.TWITTER,
      id: SocialList.TWITTER,
      url: authorStore.author?.twitter
    })
  }
  // Linkedin
  if (authorStore.author?.linkedin) {
    list.push({
      name: SocialList.LINKEDIN,
      id: SocialList.LINKEDIN,
      url: authorStore.author?.linkedin
    })
  }
  // Telegram
  if (authorStore.author?.telegram) {
    list.push({
      name: SocialList.TELEGRAM,
      id: SocialList.TELEGRAM,
      url: authorStore.author?.telegram
    })
  }
  // Facebook
  if (authorStore.author?.tiktok) {
    list.push({
      name: SocialList.TIKTOK,
      id: SocialList.TIKTOK,
      url: authorStore.author?.tiktok
    })
  }
  return list
}

/**
 * Author articles
 */
const pageArticlesCount = Number(config.public.categoryPageArticlesCount || 17)
const pageSsrArticlesCount = Number(config.public.categorySsrArticlesCount || 6)
let page = ref(Number(route.query.page || 1))

const filters: ArticleFilters = {
  authorIds: [fullAuthorId]
}

const loadAuthor = async (authorFilters: AuthorFilters) => {
  try {
    await authorStore.fetchAuthors(authorFilters, AUTHOR_QUERY_TYPE.DETAIL)
    if (!authorStore.author) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found'
      })
    }
  } catch (e) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found'
    })
  }
}

// load next page - articles
const nextPage = async () => {
  page.value = page.value + 1
  router.push({
    query: { page: page.value },
  })
  try {
    articleStore.setLimit(page.value * pageArticlesCount)
    await articleStore.fetchArticles(filters)
  } catch (e) {
    // TODO - show message to customer?
  }
}

if (import.meta.server) {
  await loadAuthor(authorFilters)
  try {
    // sever articles preload - preload pageSsrArticlesCount articles on server
    articleStore.setLimit(pageSsrArticlesCount)
    articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.PREVIEW)
  } catch (e) {
    // TODO - show message to customer?
  }
}

/**
 * On mounted - load data client
 */
onMounted(async () => {
  await loadAuthor(authorFilters)
  // client side data load

  try {
    articleStore.setLimit(page.value * pageArticlesCount)
    await articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.PREVIEW)
    articleStore.fetchDetailsForArticles(ARTICLE_QUERY_TYPE.WITH_AUTHOR)
  } catch (e) {
    // TODO - show message to customer?
  }

  /**
   * CncAds Page change
   */
  useCncAdsWrapper().changePageAds(cncAdsPage, {
    route: route
  })

})
</script>
